/**
 * Data table for record lists. Full-bleed inside a Card; header row on gray,
 * hairline row dividers, hover-revealed row actions.
 * Spec: assets/handoffs/design_system_export/specs/Table.json
 *
 * No `props` block in the spec (styling + behavior only) — the API below
 * (columns/rows/selection/sort/loading/error/emptyState) is inferred from
 * the `states`/`selection`/`sortableHeader` sections.
 *
 * @param {Array<{key:string,header:ReactNode,sortable?:boolean,align?:string,render?:(row)=>ReactNode}>} columns
 * @param {Array<object>} rows
 * @param {string='id'} rowKey
 * @param {boolean=false} selectable
 * @param {Array<string|number>=[]} selectedIds
 * @param {function=} onSelectionChange - (nextIds) => void
 * @param {{key:string,direction:'asc'|'desc'}=} sortBy
 * @param {function=} onSortChange - (nextSort) => void
 * @param {'comfortable'|'compact'=comfortable} density
 * @param {boolean=false} loading
 * @param {string=} error
 * @param {function=} onRetry
 * @param {ReactNode=} emptyState - rendered spanning all columns instead of <tbody> rows
 * @param {(row)=>ReactNode=} renderActions - hover/focus-revealed trailing actions cell
 */
import React from "react";
import classNames from "classnames";
import Icon from "../icons/Icon";
import Button from "../Button/Button";
import styles from "./Table.module.scss";

const SKELETON_WIDTHS = ["60%", "40%", "60%", "40%"];

const Table = ({
  columns,
  rows,
  rowKey = "id",
  selectable = false,
  selectedIds = [],
  onSelectionChange,
  sortBy,
  onSortChange,
  density = "comfortable",
  loading = false,
  error,
  onRetry,
  emptyState,
  renderActions,
  className,
  ...rest
}) => {
  const allSelected = selectable && rows.length > 0 && selectedIds.length === rows.length;
  const someSelected = selectable && selectedIds.length > 0 && !allSelected;
  const colSpan = columns.length + (selectable ? 1 : 0) + (renderActions ? 1 : 0);

  const toggleAll = () => {
    onSelectionChange?.(allSelected ? [] : rows.map((row) => row[rowKey]));
  };

  const toggleRow = (id) => {
    onSelectionChange?.(
      selectedIds.includes(id) ? selectedIds.filter((x) => x !== id) : [...selectedIds, id]
    );
  };

  const handleSort = (column) => {
    if (!column.sortable || !onSortChange) return;
    const direction = sortBy?.key === column.key && sortBy.direction === "asc" ? "desc" : "asc";
    onSortChange({ key: column.key, direction });
  };

  return (
    <table className={classNames(styles.table, styles[`density--${density}`], className)} {...rest}>
      <thead>
        <tr>
          {selectable && (
            <th className={styles.checkboxCell} scope="col">
              <input
                type="checkbox"
                aria-label="Select all"
                checked={allSelected}
                ref={(el) => el && (el.indeterminate = someSelected)}
                onChange={toggleAll}
              />
            </th>
          )}
          {columns.map((column) => {
            const sorted = sortBy?.key === column.key;
            return (
              <th key={column.key} scope="col" style={{ textAlign: column.align }}>
                {column.sortable ? (
                  <button
                    type="button"
                    className={styles.sortButton}
                    onClick={() => handleSort(column)}
                    aria-sort={sorted ? (sortBy.direction === "asc" ? "ascending" : "descending") : "none"}
                  >
                    {column.header}
                    <Icon
                      name="ArrowDown"
                      size={16}
                      className={classNames(styles.sortIcon, {
                        [styles["sortIcon--asc"]]: sorted && sortBy.direction === "asc",
                        [styles["sortIcon--inactive"]]: !sorted,
                      })}
                    />
                  </button>
                ) : (
                  column.header
                )}
              </th>
            );
          })}
          {renderActions && <th className={styles.actionsHeaderCell} scope="col" aria-hidden="true" />}
        </tr>
      </thead>
      <tbody>
        {error ? (
          <tr>
            <td colSpan={colSpan} className={styles.errorCell}>
              <span>{error}</span>
              {onRetry && (
                <Button hierarchy="tertiary" size="sm" onClick={onRetry}>
                  Retry
                </Button>
              )}
            </td>
          </tr>
        ) : loading ? (
          SKELETON_WIDTHS.map((width, i) => (
            <tr key={i}>
              <td colSpan={colSpan}>
                <div className={styles.skeletonBar} style={{ width }} />
              </td>
            </tr>
          ))
        ) : rows.length === 0 && emptyState ? (
          <tr>
            <td colSpan={colSpan} className={styles.emptyCell}>
              {emptyState}
            </td>
          </tr>
        ) : (
          rows.map((row) => {
            const id = row[rowKey];
            const selected = selectedIds.includes(id);
            return (
              <tr key={id} className={classNames(styles.row, { [styles["row--selected"]]: selected })}>
                {selectable && (
                  <td className={styles.checkboxCell}>
                    <input
                      type="checkbox"
                      aria-label={`Select row ${id}`}
                      checked={selected}
                      onChange={() => toggleRow(id)}
                    />
                  </td>
                )}
                {columns.map((column) => (
                  <td key={column.key} style={{ textAlign: column.align }}>
                    {column.render ? column.render(row) : row[column.key]}
                  </td>
                ))}
                {renderActions && <td className={styles.actionsCell}>{renderActions(row)}</td>}
              </tr>
            );
          })
        )}
      </tbody>
    </table>
  );
};

export default Table;
